// Excel-style column filters on every table in the hub and the portal (Justin, 12 Oct: "can we add filters and selections
// to each of the columns, much like excel!"). One enhancer for every table, so each page keeps its own markup:
// a menu button on each column header with Sort A→Z / Z→A, a search box and a checklist of the column's values with
// counts. Rows that don't match are hidden; a caption says how many rows show and clears every filter. A table opts out
// with data-xf="off". Filters apply to the rows on screen (a paged table filters its current page) and last while the
// page stays open.

export const BLANK = '(Blanks)';

/** A cell's value as the filter sees it: its text with spaces collapsed; an empty cell or a dash is "(Blanks)". */
export function cellValue(text: string): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t === '' || t === '—' || t === '–' || t === '-' ? BLANK : t;
}

/** A number in a cell, if the whole value is one: 1,234 · $1,234.50 · -12% · 3.5 · (1,200). */
export function parseNumber(value: string): number | null {
  const t = value.replace(/[\s ]/g, '').replace(/[−–]/g, '-');
  const m = /^(\()?([-+]?)\$?(\d{1,3}(?:,\d{3})+|\d*)(\.\d+)?(%)?(\))?$/.exec(t);
  if (!m || (!m[3] && !m[4]) || Boolean(m[1]) !== Boolean(m[6])) return null;
  const n = Number(`${m[2]}${m[3].replace(/,/g, '')}${m[4] ?? ''}`);
  if (!Number.isFinite(n)) return null;
  return m[1] ? -n : n;
}

/** A date or date-time in a cell, as a timestamp: "Oct 12", "Oct 12, 1:33 PM", "Oct 12, 2026", "2026-10-12", "10/12/2026". */
export function parseDate(value: string): number | null {
  if (!/^(?:(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]* \d{1,2}\b|\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})/.test(value)) return null;
  const t = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
  return Number.isFinite(t) ? t : null;
}

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/** Blanks last; numbers by value; dates in date order; everything else in natural order (Site 2 before Site 10). */
export function compareValues(a: string, b: string): number {
  if (a === b) return 0;
  if (a === BLANK) return 1;
  if (b === BLANK) return -1;
  const x = parseNumber(a), y = parseNumber(b);
  if (x !== null && y !== null) return x - y || collator.compare(a, b);
  if (x !== null) return -1;
  if (y !== null) return 1;
  const d = parseDate(a), e = parseDate(b);
  if (d !== null && e !== null) return d - e || collator.compare(a, b);
  return collator.compare(a, b);
}

/** A column's distinct values with how many rows hold each, in sort order. */
export function distinctValues(values: string[]): { value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts].map(([value, count]) => ({ value, count })).sort((p, q) => compareValues(p.value, q.value));
}

export type Filters = Map<number, Set<string>>;

/** A row shows when every filtered column holds one of its chosen values. `skip` leaves one column out. */
export function rowMatches(cells: string[], filters: Filters, skip = -1): boolean {
  for (const [col, allowed] of filters) if (col !== skip && !allowed.has(cells[col] ?? BLANK)) return false;
  return true;
}

/** Whether a column's values are mostly numbers, so the menu says "smallest to largest". */
export function numericColumn(values: string[]): boolean {
  const filled = values.filter(v => v !== BLANK);
  return filled.length > 0 && filled.filter(v => parseNumber(v) !== null).length / filled.length >= 0.8;
}

/** The values a menu leaves chosen: the ticked ones, and while searching only the ticked ones that match. */
export function chosenValues(all: string[], ticked: Set<string>, search: string): Set<string> {
  const q = search.trim().toLowerCase();
  return new Set(all.filter(v => ticked.has(v) && (!q || v.toLowerCase().includes(q))));
}

// The DOM side ---------------------------------------------------------------------------------------------------------

type Sort = { col: number; dir: 1 | -1 };
type State = { filters: Filters; sort: Sort | null };

const saved = new Map<string, State>();
const states = new WeakMap<HTMLTableElement, State>();
const ICON = '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M2 3h12l-4.6 5.4V13l-2.8 1.4V8.4z" fill="currentColor"/></svg>';

function cellText(cell: Element): string {
  const fields = [...cell.querySelectorAll('input:not([type=checkbox]):not([type=radio]),select,textarea')] as (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[];
  if (fields.length && !(cell.textContent ?? '').trim()) {
    return fields.map(f => (f instanceof HTMLSelectElement ? f.selectedOptions[0]?.text ?? f.value : f.value)).join(' ');
  }
  // Text nodes joined with spaces, so a name and a company on separate lines don't run together.
  const parts: string[] = [];
  const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT, {
    acceptNode: n => (n.parentElement?.closest('.xf-btn,[aria-hidden="true"],.sr-only,.visually-hidden') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let n = walker.nextNode(); n; n = walker.nextNode()) parts.push(n.nodeValue ?? '');
  return parts.join(' ');
}

function headerCells(table: HTMLTableElement): HTMLTableCellElement[] {
  const head = table.tHead;
  if (!head || !head.rows.length) return [];
  return [...head.rows[head.rows.length - 1].cells];
}

/** Body rows that hold one cell per column; group rows and "no results" rows are left alone. */
function dataRows(table: HTMLTableElement, columns: number): HTMLTableRowElement[] {
  const rows: HTMLTableRowElement[] = [];
  for (const body of table.tBodies) for (const row of body.rows) if (row.cells.length === columns) rows.push(row);
  return rows;
}

function headerLabel(th: HTMLTableCellElement): string {
  return cellValue(cellText(th)).replace(BLANK, '');
}

function tableKey(root: HTMLElement, table: HTMLTableElement, page: string): string {
  const index = [...root.querySelectorAll('table')].indexOf(table);
  return `${page}|${index}|${headerCells(table).map(headerLabel).join('|')}`;
}

export function enhanceTables(root: HTMLElement, page: () => string = () => location.search): () => void {
  let menu: HTMLDivElement | null = null;
  let menuFor: HTMLButtonElement | null = null;
  let frame = 0;

  const observer = new MutationObserver(records => {
    if (records.every(r => r.target instanceof Element && r.target.closest('.xf-status'))) return;
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; scan(); });
  });
  const quiet = (fn: () => void) => {
    observer.disconnect();
    try { fn(); } finally { observer.observe(root, { childList: true, subtree: true, characterData: true }); }
  };

  function stateOf(table: HTMLTableElement): State {
    let s = states.get(table);
    if (!s) {
      const key = tableKey(root, table, page());
      s = saved.get(key) ?? { filters: new Map(), sort: null };
      states.set(table, s);
      saved.set(key, s);
    }
    return s;
  }

  function columnValues(table: HTMLTableElement, col: number, rows: HTMLTableRowElement[], filters: Filters) {
    const cols = headerCells(table).length;
    return rows
      .map(r => [...r.cells].map(c => cellValue(cellText(c))))
      .filter(cells => cells.length === cols && rowMatches(cells, filters, col))
      .map(cells => cells[col] ?? BLANK);
  }

  function apply(table: HTMLTableElement) {
    const s = stateOf(table);
    const heads = headerCells(table);
    const rows = dataRows(table, heads.length);
    quiet(() => {
      let shown = 0;
      for (const row of rows) {
        const cells = [...row.cells].map(c => cellValue(cellText(c)));
        const visible = rowMatches(cells, s.filters);
        if (visible) shown++;
        if (row.hidden === visible) row.hidden = !visible;
        row.classList.toggle('xf-hidden', !visible);
      }
      if (s.sort) {
        const { col, dir } = s.sort;
        for (const body of table.tBodies) {
          const mine = [...body.rows].filter(r => r.cells.length === heads.length);
          if (mine.length < 2 || mine.length !== body.rows.length) continue;
          const sorted = [...mine].sort((a, b) => dir * compareValues(cellValue(cellText(a.cells[col])), cellValue(cellText(b.cells[col]))));
          if (sorted.some((r, i) => r !== mine[i])) for (const r of sorted) body.appendChild(r);
        }
      }
      heads.forEach((th, i) => th.querySelector('.xf-btn')?.classList.toggle('is-on', s.filters.has(i) || s.sort?.col === i));
      let caption = table.querySelector(':scope > caption.xf-status') as HTMLTableCaptionElement | null;
      if (s.filters.size) {
        if (!caption) {
          caption = document.createElement('caption');
          caption.className = 'xf-status';
          table.insertBefore(caption, table.firstChild);
        }
        caption.replaceChildren();
        const text = document.createElement('span');
        text.textContent = `Showing ${shown.toLocaleString('en-US')} of ${rows.length.toLocaleString('en-US')} rows`;
        const clear = document.createElement('button');
        clear.type = 'button';
        clear.textContent = 'Clear filters';
        clear.addEventListener('click', e => { e.stopPropagation(); s.filters.clear(); apply(table); });
        caption.append(text, clear);
      } else caption?.remove();
    });
  }

  /** Puts the open menu under its button (above it when there is no room), and closes it once the button scrolls away. */
  function place() {
    if (!menu || !menuFor) return;
    const r = menuFor.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight || (!r.width && !r.height)) { closeMenu(false); return; }
    const width = Math.min(300, window.innerWidth - 16);
    menu.style.width = `${width}px`;
    menu.style.left = `${Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8))}px`;
    const below = r.bottom + 4;
    menu.style.top = `${Math.max(8, below + menu.offsetHeight > window.innerHeight - 8 ? r.top - menu.offsetHeight - 4 : below)}px`;
  }

  function closeMenu(returnFocus = true) {
    if (!menu) return;
    menu.remove();
    menu = null;
    document.removeEventListener('pointerdown', outside, true);
    if (returnFocus) menuFor?.focus();
    menuFor?.setAttribute('aria-expanded', 'false');
    menuFor = null;
  }
  function outside(e: Event) {
    if (menu && !menu.contains(e.target as Node) && e.target !== menuFor) closeMenu(false);
  }

  function openMenu(table: HTMLTableElement, col: number, button: HTMLButtonElement) {
    if (menuFor === button) { closeMenu(); return; }
    closeMenu(false);
    const s = stateOf(table);
    const heads = headerCells(table);
    const label = headerLabel(heads[col]) || `Column ${col + 1}`;
    const rows = dataRows(table, heads.length);
    const values = columnValues(table, col, rows, s.filters);
    const chosen = s.filters.get(col);
    const listed = distinctValues(chosen ? [...values, ...[...chosen].filter(v => !values.includes(v))] : values);
    const all = listed.map(v => v.value);
    const ticked = new Set(chosen ? all.filter(v => chosen.has(v)) : all);
    const numeric = numericColumn(values);

    const el = document.createElement('div');
    el.className = 'xf-menu';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', `Filter ${label}`);
    const button_ = (text: string, onClick: () => void, cls = 'xf-action') => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = cls;
      b.textContent = text;
      b.addEventListener('click', onClick);
      return b;
    };
    const sortTo = (dir: 1 | -1) => () => { s.sort = { col, dir }; apply(table); closeMenu(); };
    const actions = document.createElement('div');
    actions.className = 'xf-actions';
    actions.append(
      button_(numeric ? 'Sort smallest to largest' : 'Sort A to Z', sortTo(1)),
      button_(numeric ? 'Sort largest to smallest' : 'Sort Z to A', sortTo(-1)),
    );
    if (s.sort?.col === col) actions.append(button_('Clear sort', () => { s.sort = null; apply(table); closeMenu(); }));
    const clearOne = button_(`Clear filter from “${label}”`, () => { s.filters.delete(col); apply(table); closeMenu(); });
    clearOne.disabled = !chosen;
    actions.append(clearOne);

    const search = document.createElement('input');
    search.type = 'search';
    search.className = 'xf-search';
    search.placeholder = 'Search';
    search.setAttribute('aria-label', `Search ${label} values`);

    const list = document.createElement('div');
    list.className = 'xf-list';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-label', `${label} values`);
    const allBox = document.createElement('input');
    allBox.type = 'checkbox';
    const MAX = 500;
    const draw = () => {
      const q = search.value.trim().toLowerCase();
      const shown = listed.filter(v => !q || v.value.toLowerCase().includes(q));
      list.replaceChildren();
      const head = document.createElement('label');
      head.className = 'xf-item xf-item--all';
      const allTicked = shown.length > 0 && shown.every(v => ticked.has(v.value));
      allBox.checked = allTicked;
      allBox.indeterminate = !allTicked && shown.some(v => ticked.has(v.value));
      head.append(allBox, document.createTextNode(q ? '(Select all search results)' : '(Select all)'));
      list.append(head);
      for (const v of shown.slice(0, MAX)) {
        const item = document.createElement('label');
        item.className = 'xf-item';
        const box = document.createElement('input');
        box.type = 'checkbox';
        box.checked = ticked.has(v.value);
        box.addEventListener('change', () => { if (box.checked) ticked.add(v.value); else ticked.delete(v.value); draw(); });
        const name = document.createElement('span');
        name.textContent = v.value;
        const n = document.createElement('small');
        n.textContent = v.count.toLocaleString('en-US');
        item.append(box, name, n);
        list.append(item);
      }
      if (shown.length > MAX) {
        const more = document.createElement('p');
        more.className = 'xf-more';
        more.textContent = `${(shown.length - MAX).toLocaleString('en-US')} more: search to narrow the list.`;
        list.append(more);
      }
      if (!shown.length) {
        const none = document.createElement('p');
        none.className = 'xf-more';
        none.textContent = 'No values match.';
        list.append(none);
      }
    };
    allBox.addEventListener('change', () => {
      const q = search.value.trim().toLowerCase();
      for (const v of listed) if (!q || v.value.toLowerCase().includes(q)) { if (allBox.checked) ticked.add(v.value); else ticked.delete(v.value); }
      draw();
    });
    search.addEventListener('input', draw);
    draw();

    const ok = button_('OK', () => {
      const keep = chosenValues(all, ticked, search.value);
      if (keep.size === all.length) s.filters.delete(col); else s.filters.set(col, keep);
      apply(table);
      closeMenu();
    }, 'button button--primary');
    const cancel = button_('Cancel', () => closeMenu(), 'button button--quiet');
    const foot = document.createElement('div');
    foot.className = 'xf-foot';
    foot.append(cancel, ok);
    el.append(actions, search, list, foot);
    el.addEventListener('keydown', e => {
      if (e.key === 'Escape') { e.preventDefault(); closeMenu(); }
      if (e.key === 'Enter' && e.target === search) { e.preventDefault(); ok.click(); }
      e.stopPropagation();
    });
    el.addEventListener('click', e => e.stopPropagation());

    document.body.append(el);
    menu = el;
    menuFor = button;
    place();
    button.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', outside, true);
    search.focus();
  }

  function enhance(table: HTMLTableElement) {
    if (table.dataset.xf === 'off' || table.closest('[data-xf="off"]')) return;
    const heads = headerCells(table);
    if (!heads.length || heads.some(th => th.colSpan > 1)) return;
    if (dataRows(table, heads.length).length < 2 && !stateOf(table).filters.size) return;
    quiet(() => {
      heads.forEach((th, col) => {
        if (th.querySelector(':scope > .xf-btn')) return;
        const label = headerLabel(th);
        if (!label) return;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'xf-btn';
        b.innerHTML = ICON;
        b.title = `Filter or sort ${label}`;
        b.setAttribute('aria-label', `Filter or sort ${label}`);
        b.setAttribute('aria-haspopup', 'dialog');
        b.setAttribute('aria-expanded', 'false');
        b.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); openMenu(table, col, b); });
        b.addEventListener('keydown', e => e.stopPropagation());
        th.classList.add('xf-th');
        th.append(b);
      });
    });
    const s = stateOf(table);
    if (s.filters.size || s.sort) apply(table);
  }

  function scan() {
    for (const table of root.querySelectorAll('table')) enhance(table as HTMLTableElement);
    if (menuFor && !root.contains(menuFor)) closeMenu(false);
  }

  const onScroll = (e: Event) => { if (menu && !menu.contains(e.target as Node)) place(); };
  window.addEventListener('resize', onScroll);
  window.addEventListener('scroll', onScroll, true);
  observer.observe(root, { childList: true, subtree: true, characterData: true });
  scan();
  return () => {
    observer.disconnect();
    if (frame) cancelAnimationFrame(frame);
    closeMenu(false);
    window.removeEventListener('resize', onScroll);
    window.removeEventListener('scroll', onScroll, true);
  };
}
